import { useState } from "react";
import { C, FONT, acGlow, logoDot } from "../theme";
import { Dots } from "../components/Dots";
import { useToast } from "../components/Toast";
import { requestOtp, verifyOtp, type Me } from "../lib/api";

type Step = "email" | "otp";

export function Login({
  email,
  setEmail,
  onBack,
  onSuccess,
  initialEmail,
}: {
  email: string;
  setEmail: (v: string) => void;
  onBack: () => void;
  onSuccess: (me: Me) => void;
  initialEmail?: boolean;
}) {
  const [step, setStep] = useState<Step>("email");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const sendCode = async () => {
    if (!/.+@.+\..+/.test(email)) {
      toast("Enter a valid email address");
      return;
    }
    setBusy(true);
    try {
      await requestOtp(email);
      setStep("otp");
      toast("Code sent — check your email");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Could not send code");
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (otp.length !== 6) {
      toast("Enter the 6-digit code");
      return;
    }
    setBusy(true);
    try {
      const me = await verifyOtp(email, otp);
      onSuccess(me);
    } catch (e) {
      toast(e instanceof Error ? e.message : "Invalid or expired code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
      <div style={{ width: 420, maxWidth: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 11, justifyContent: "center", fontWeight: 700, color: C.textBright, fontSize: 16, marginBottom: 26 }}>
          <span style={logoDot} />geo.zynday.com
        </div>
        <div style={{ background: C.panel, border: `1px solid ${C.line2}`, borderRadius: 12, padding: 34, animation: "fadeup .3s ease" }}>
          {step === "email" ? (
            <>
              <div style={{ color: C.textBrightest, fontWeight: 700, fontSize: 18, marginBottom: 6 }}>Sign in</div>
              <div style={{ color: C.muted, fontSize: 13, marginBottom: 24 }}>
                {initialEmail ? "We emailed your login code — or send a new one." : "We'll email you a one-time login code."}
              </div>
              <label style={{ color: C.muted, fontSize: 12, display: "block", marginBottom: 7 }}>Email</label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendCode()}
                placeholder="you@company.com"
                style={{ width: "100%", background: C.bg, border: `1px solid ${C.line3}`, borderRadius: 7, padding: 12, color: C.textBrightest, font: `500 13px ${FONT}`, marginBottom: 18 }}
              />
              <button onClick={sendCode} disabled={busy} style={{ width: "100%", background: C.ac, border: "none", color: C.acInk, padding: 13, borderRadius: 8, font: `700 14px ${FONT}`, cursor: "pointer", boxShadow: acGlow(0.3), opacity: busy ? 0.7 : 1 }}>
                {busy ? "Sending…" : <>Send login code <Dots /></>}
              </button>
              <div style={{ textAlign: "center", marginTop: 18 }}>
                <button onClick={onBack} style={linkBtn}>← back to site</button>
              </div>
            </>
          ) : (
            <>
              <div style={{ color: C.textBrightest, fontWeight: 700, fontSize: 18, marginBottom: 6 }}>Enter your code</div>
              <div style={{ color: C.muted, fontSize: 13, marginBottom: 24 }}>
                Sent to <span style={{ color: C.text }}>{email}</span>
              </div>
              <input
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={(e) => e.key === "Enter" && verify()}
                inputMode="numeric"
                maxLength={6}
                placeholder="······"
                style={{ width: "100%", background: C.bg, border: `1px solid ${C.line3}`, borderRadius: 8, padding: 16, color: C.ac, font: `700 30px ${FONT}`, textAlign: "center", letterSpacing: 16, marginBottom: 18 }}
              />
              <button onClick={verify} disabled={busy} style={{ width: "100%", background: C.ac, border: "none", color: C.acInk, padding: 13, borderRadius: 8, font: `700 14px ${FONT}`, cursor: "pointer", boxShadow: acGlow(0.3), opacity: busy ? 0.7 : 1 }}>
                {busy ? "Verifying…" : <>Verify & continue <Dots /></>}
              </button>
              <div style={{ textAlign: "center", marginTop: 18, display: "flex", gap: 16, justifyContent: "center" }}>
                <button onClick={() => { setStep("email"); setOtp(""); }} style={linkBtn}>← change email</button>
                <button onClick={sendCode} style={linkBtn}>resend code</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const linkBtn: React.CSSProperties = {
  background: "none",
  border: "none",
  color: C.muted,
  font: `400 12px ${FONT}`,
  cursor: "pointer",
  textDecoration: "underline",
};
